import { fetchJobMEEPsMETAPsInfos, fetchExposures } from "./referentielService";

// Calcule l'éligibilité à la visite de fin de carrière (VFC) à partir d'une liste de métiers.
//
// Logique métier (cf. champ `eligibleVFC` porté par chaque exposition) :
//   1. Pour chaque métier sélectionné, on récupère les expositions potentielles via les
//      matrices métier→exposition (endpoint public /job/meeps-metaps-infos), TOUTES sources
//      confondues (PRESANSE, RAVAIL…). Le verdict doit rester exhaustif : de nombreux métiers
//      n'ont leurs liens d'exposition que dans la matrice RAVAIL (ex. Ébéniste → poussière de
//      bois / formaldéhyde) — les restreindre à Présanse rendrait ces métiers faussement non
//      éligibles.
//   2. On récupère le détail de l'union de ces expositions (endpoint public /exposure/details),
//      qui porte les drapeaux `eligibleVFC` et `suiviPostProfessionnel`.
//   3. Le salarié est éligible à une VFC dès qu'au moins une de ses expositions est `eligibleVFC`.
//
// `jobs` : tableau de métiers { id, label }
// Retourne :
//   - eligible : booléen
//   - eligibleExposures : liste plate dédoublonnée des expositions éligibles (pour l'email/le PDF)
//   - jobsWithExposures : [{ id, label, exposures: [{ id, libelle }] }] — uniquement les métiers
//     ayant au moins une exposition éligible, pour un affichage regroupé par métier.
export async function computeEligibility(jobs, onError) {
  // 1. Expositions potentielles (clés, par métier + union globale, TOUTES sources — cf. verdict)
  //    ET tâches potentielles (libellés directs, dédoublonnées) par métier. Pour les tâches
  //    on ne retient que la source Présanse : les tâches MP (tableaux de maladies
  //    professionnelles) ne doivent pas apparaître dans le récapitulatif public.
  const jobData = []; // [{ id, label, keys: Set, tasks: [libellés] }]
  const allKeys = new Set();
  for (const job of jobs) {
    const infos = await fetchJobMEEPsMETAPsInfos(job.id, null, onError);
    const keys = new Set();
    const taskSet = new Set();
    if (infos) {
      if (infos.potentialExposures) {
        for (const source of Object.keys(infos.potentialExposures)) {
          (infos.potentialExposures[source] || []).forEach((key) => {
            keys.add(key);
            allKeys.add(key);
          });
        }
      }
      if (infos.potentialTasks) {
        (infos.potentialTasks.PRESANSE || []).forEach((task) => taskSet.add(task));
      }
    }
    jobData.push({ id: job.id, label: job.label, keys, tasks: [...taskSet] });
  }

  if (allKeys.size === 0) {
    return { eligible: false, eligibleExposures: [], jobsWithExposures: [] };
  }

  // 2. Détail des expositions → on ne garde que les eligibleVFC, indexées par clé
  const details = await fetchExposures([...allKeys], onError);
  const eligibleByKey = new Map();
  (Array.isArray(details) ? details : []).forEach((e) => {
    if (e && e.id && e.eligibleVFC) eligibleByKey.set(e.id, e);
  });

  // 3. Liste plate dédoublonnée par libellé (email + PDF)
  const seen = new Set();
  const eligibleExposures = [...eligibleByKey.values()].filter((e) => {
    const k = e.libelle || e.id;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  // 4. Regroupement par métier : expositions éligibles propres à chaque métier (+ ses tâches)
  const jobsWithExposures = jobData
    .map(({ id, label, keys, tasks }) => {
      const seenJob = new Set();
      const exposures = [...keys]
        .map((k) => eligibleByKey.get(k))
        .filter(Boolean)
        .filter((e) => {
          const lk = e.libelle || e.id;
          if (seenJob.has(lk)) return false;
          seenJob.add(lk);
          return true;
        })
        .map((e) => ({ id: e.id, libelle: e.libelle }));
      return { id, label, exposures, tasks };
    })
    .filter((job) => job.exposures.length > 0);

  return {
    eligible: eligibleExposures.length > 0,
    eligibleExposures,
    jobsWithExposures,
  };
}
